import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Navigate } from "react-router-dom";
import { getBySlug, getProducts } from "../utils/api";
import { useAuth } from "../hooks/useAuth";
import AffiliateDisclosure from "../components/AffiliateDisclosure";
import FilterBar from "../components/FilterBar";
import ProductGrid from "../components/ProductGrid";

function SEO({ sf }) {
  useEffect(() => {
    document.title = sf.seo_title || sf.title;
    const set = (n, c) => {
      let el = document.querySelector(`meta[name="${n}"]`);
      if (!el) { el = document.createElement("meta"); el.name = n; document.head.appendChild(el); }
      el.content = c;
    };
    set("description", sf.seo_desc || sf.description || "");
    set("robots", "index, follow");
  }, [sf]);
  return null;
}

export default function StorefrontPage() {
  const { slug }   = useParams();
  const { isAdmin } = useAuth();

  const [sf, setSf]             = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [prodLoading, setProdLoading] = useState(false);
  const [error, setError]       = useState("");
  const [filters, setFilters]   = useState({ brand:"", minPrice:0, maxPrice:9999, minRating:0 });
  const [sort, setSort]         = useState("pop");

  // Track clicks (lightweight, no external dep)
  const trackClick = useCallback((product) => {
    console.info("[click]", product.asin, product.title);
    // Swap in GA4/Plausible here:
    // window.gtag?.('event','select_content',{content_type:'product',item_id:product.asin});
  }, []);

  useEffect(() => {
    setLoading(true);
    getBySlug(slug)
      .then(async (data) => {
        setSf(data);
        setProdLoading(true);
        try {
          const { products: prods } = await getProducts(data.id, { isAdmin });
          setProducts(prods);
        } catch (e) {
          setError(e.message);
        } finally { setProdLoading(false); }
      })
      .catch(() => setError("404"))
      .finally(() => setLoading(false));
  }, [slug, isAdmin]);

  const brands = useMemo(() =>
    [...new Set(products.map(p => p.brand).filter(Boolean))].sort(),
  [products]);

  const filtered = useMemo(() => {
    let r = products.filter(p => {
      if (filters.brand    && p.brand  !== filters.brand)    return false;
      if (p.price != null  && p.price   < filters.minPrice)   return false;
      if (p.price != null  && p.price   > filters.maxPrice)   return false;
      if (p.rating != null && p.rating  < filters.minRating)  return false;
      return true;
    });
    return [...r].sort((a,b) => {
      if (sort === "price_a") return (a.price??0) - (b.price??0);
      if (sort === "price_d") return (b.price??0) - (a.price??0);
      if (sort === "rating")  return (b.rating??0) - (a.rating??0);
      return (b.popularityScore??0) - (a.popularityScore??0);
    });
  }, [products, filters, sort]);

  if (loading) return (
    <div className="loading-block container"><span className="spinner spinner-dark" /> Loading…</div>
  );
  if (error === "404") return <Navigate to="/" replace />;
  if (error) return <div className="container" style={{padding:"40px 0",color:"var(--danger)"}}>⚠ {error}</div>;
  if (!sf) return null;

  const publishedDate = sf.published_at
    ? new Date(sf.published_at).toLocaleDateString("en-GB", {day:"numeric",month:"long",year:"numeric"})
    : null;

  return (
    <>
      <SEO sf={sf} />
      <div className="container main-area">
        <AffiliateDisclosure />

        <section className="sf-hero">
          <h1 className="sf-title">{sf.title}</h1>
          {sf.subtitle    && <p className="sf-subtitle">{sf.subtitle}</p>}
          {sf.description && <p className="sf-desc">{sf.description}</p>}
          <div className="sf-meta">
            {sf.category     && <span>📦 {sf.category}</span>}
            {publishedDate   && <span>🗓 Updated {publishedDate}</span>}
            <span>🌐 Live page</span>
          </div>
        </section>

        {prodLoading ? (
          <div className="loading-block"><span className="spinner spinner-dark" /> Fetching products from Amazon…</div>
        ) : (
          <>
            <FilterBar
              brands={brands} filters={filters} sort={sort}
              onChange={setFilters} onSort={setSort}
              shown={filtered.length} total={products.length}
            />
            <ProductGrid products={filtered} onCTAClick={trackClick} />
          </>
        )}
      </div>
    </>
  );
}
